// Client-side ground: turns the server's one-off "terrain" message into a mesh, and answers
// "how high is the ground here" for the walking camera.
//
// The heightfield is generated server-side (terrain.py) from world.json and is the same
// surface MuJoCo uses for contact and ecology uses to seed plants, so what you see is what
// the quadruped stands on. A world without a terrain block never sends the message and the
// renderer keeps its flat plane.
//
// Surface detail: the mesh is only 1 m per vertex, so all fine structure comes from the
// fragment shader. It samples a seamless CC0 rippled-sand texture (groundAssets.ts) in world
// space at two scales, hides the tiling with hex-grid stochastic blending (rotations are kept
// small so the ripples share a heading), and modulates the result with the vertex palette (damp
// sand near the sea, dry sand above it, windswept slopes) and a little world-space noise. A wet
// band follows the waterline: sand at and below the tide's recent high mark is darker and
// glossier, and dries slowly as the tide falls (see setWetLevel). If the textures have not
// decoded yet the ground shows the palette plus noise.
//
// Island worlds have ground BELOW z = 0 (the seabed). The sea itself is drawn by waterView.ts.
//
// Sim (x, y, z) maps to three (x, z, -y). Samples are at grid corners, row = sim y.
import * as THREE from 'three';
import { GROUND_TEXTURES, GROUND_STATS } from './groundAssets';
import { CanopyShade, CANOPY_SHADE_DECL, canopyShadeApply } from './canopyShade';

export interface TerrainMessage {
  type: 'terrain';
  nx: number; ny: number;
  width: number; height: number;
  z: string;              // base64, float32 little-endian, row-major (row = sim y)
  sea_level?: number;     // z of the sea surface before tides (absent on an older server: no sea)
  tide_amplitude?: number;
}

// Ground tint (hex sRGB, like the rest of the scene's palette).
const SOIL_LOW   = 0x9a8a68;   // damp sand at and below the waterline
const SOIL_HIGH  = 0xd2c39a;   // dry sand on the back beach and dunes
const SLOPE_TINT = 0xe0d3ae;   // windswept, paler sand on steeper dune faces
const WET_BAND_BELOW = 0.20;   // palette: metres below the sea level that count as fully damp
const WET_BAND_ABOVE = 1.00;   // palette: metres above it over which the sand dries to full colour

// Ground surface tuning. Ground082L has no real-world size; TILE_M is a judgement call.
const GROUND_TILE_M      = 1.6;    // metres covered by one texture repeat (main layer)
const GROUND_FINE_TILE_M = 0.40;   // metres per repeat of the fine overlay layer
const GROUND_ROT_RANGE   = 0.7;    // radians: total spread of the hex tiles' random rotation (about +-20 deg), so ripples keep a shared heading
const GROUND_NORMAL      = 1.0;    // normal-map strength (1 = as authored)
const GROUND_MACRO_MIX   = 0.65;   // 0 = texture colour only, 1 = fully follow the vertex palette's light/dark
const GROUND_FINE_LUM    = 0.55;   // how much the fine layer modulates brightness
const GROUND_AO          = 0.6;    // how much of the baked micro-occlusion to apply
const GROUND_ROUGH_MIN   = 0.80;   // roughness floor (dry sand is matte; the texture's roughness map adds a little)
const GROUND_SPECULAR    = 0.25;   // scales ALL specular on the ground (sun glint, sky and grazing-angle sheen); 1 = stock, 0 = none
const GROUND_ALBEDO      = 0.62;   // scales the sand texture's albedo. The map is a bright real-world sand (about 0.5 linear); the scene's light
                                   // gains were tuned on dark forest floor, so this keeps midday sand from clipping. Raise it for brighter beaches.
const WET_DARKEN         = 0.58;   // brightness of fully wet sand relative to dry
const WET_ROUGH          = 0.55;   // roughness multiplier on wet sand
const WET_SPEC           = 3.2;    // specular multiplier on wet sand
const WET_SOAK_BELOW     = 0.02;   // metres: sand this far under the wet level is fully soaked
const WET_FADE_ABOVE     = 0.10;   // metres: the soaked look fades out over this height above it
const VERTEX_MOTTLE      = 0.03;   // per-vertex brightness jitter (fine detail now comes from the shader)
// Distances (metres from the camera) over which detail fades to the texture average.
// The fine layer costs 6 more gradient texture fetches per pixel (three hex corners each for colour
// and normal), so it is confined to where a 0.45 m tile is actually large on screen.
const FINE_FADE: [number, number]   = [4, 15];
const NORMAL_FADE: [number, number] = [35, 110];
const COLOR_FADE: [number, number]  = [60, 200];

function hash2(i: number, j: number): number {
  let h = (Math.imul(i, 0x27d4eb2d) ^ Math.imul(j, 0x165667b1)) >>> 0;
  h ^= h >>> 15; h = Math.imul(h, 0x2c1b3c6d) >>> 0; h ^= h >>> 12;
  return (h >>> 0) / 4294967296;
}

// ---------------------------------------------------------------------------------------
// Ground shader (injected into MeshStandardMaterial so lights, shadows and fog still work).
// ---------------------------------------------------------------------------------------
const GROUND_VERT_COMMON = /* glsl */`
#include <common>
varying vec3 vGPos;
`;
const GROUND_VERT_BEGIN = /* glsl */`
#include <begin_vertex>
vGPos = (modelMatrix * vec4(transformed, 1.0)).xyz;
`;

const GROUND_FRAG_COMMON = /* glsl */`
#include <common>
${CANOPY_SHADE_DECL}
varying vec3 vGPos;
uniform sampler2D uGDiff;
uniform sampler2D uGNor;
uniform sampler2D uGRma;
uniform vec3  uGMean;        // linear average colour of the diffuse map
uniform float uGMeanLum;
uniform vec2  uGMeanRma;     // average AO, roughness
uniform vec3  uGMacroMean;   // average vertex colour, to turn the palette into a relative light/dark
uniform float uGTile;        // 1 / metres per repeat
uniform float uGFineTile;
uniform float uGNormal;
uniform float uGMacroMix;
uniform float uGFineLum;
uniform float uGAo;
uniform float uGRoughMin;
uniform float uGSpec;
uniform vec2  uGFineFade;
uniform vec2  uGNormalFade;
uniform vec2  uGColorFade;
uniform float uGReady;       // 1 once all three textures have decoded
uniform float uGWetY;        // three-y of the wet level: the sea's recent high mark, easing down as the tide falls
uniform float uGRotRange;    // radians of random rotation per hex tile
uniform float uGAlbedo;      // overall albedo scale on the sand texture

// Filled in at the colour stage, used at the roughness and normal stages.
float gRoughF;
float gWet;
vec3  gNormTS;

vec2 gHash22(vec2 p) {
  vec3 p3 = fract(vec3(p.xyx) * vec3(0.1031, 0.1030, 0.0973));
  p3 += dot(p3, p3.yzx + 33.33);
  return fract((p3.xx + p3.yz) * p3.zy);
}
float gHash21(vec2 p) {
  vec3 p3 = fract(vec3(p.xyx) * 0.1031);
  p3 += dot(p3, p3.yzx + 33.33);
  return fract((p3.x + p3.y) * p3.z);
}
float gVnoise(vec2 p) {
  vec2 i = floor(p), f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  return mix(mix(gHash21(i), gHash21(i + vec2(1.0, 0.0)), f.x),
             mix(gHash21(i + vec2(0.0, 1.0)), gHash21(i + vec2(1.0, 1.0)), f.x), f.y);
}

// Hex-grid stochastic tiling: each point blends three randomly offset and rotated copies of the
// texture (the three corners of the triangle of the lattice it falls in), so no repeat is visible.
struct GTri {
  vec2 uv0; vec2 uv1; vec2 uv2;     // offset, rotated coordinates for each corner
  mat2 r0;  mat2 r1;  mat2 r2;      // their rotations
  vec3 w;                           // normalised blend weights
};
mat2 gRot(float a) { float c = cos(a), s = sin(a); return mat2(c, s, -s, c); }
GTri gMakeTri(vec2 uv) {
  vec2 p = uv * 3.4641016;                                           // 2 * sqrt(3): lattice cells per repeat
  vec2 sk = vec2(p.x, -0.57735027 * p.x + 1.15470054 * p.y);          // skew to a triangular lattice
  vec2 base = floor(sk);
  vec3 t = vec3(fract(sk), 0.0);
  t.z = 1.0 - t.x - t.y;
  vec3 w; vec2 v0, v1, v2;
  if (t.z > 0.0) {
    w = vec3(t.z, t.y, t.x);
    v0 = base; v1 = base + vec2(0.0, 1.0); v2 = base + vec2(1.0, 0.0);
  } else {
    w = vec3(-t.z, 1.0 - t.y, 1.0 - t.x);
    v0 = base + vec2(1.0, 1.0); v1 = base + vec2(1.0, 0.0); v2 = base + vec2(0.0, 1.0);
  }
  GTri s;
  s.r0 = gRot((gHash22(v0).x - 0.5) * uGRotRange);
  s.r1 = gRot((gHash22(v1).x - 0.5) * uGRotRange);
  s.r2 = gRot((gHash22(v2).x - 0.5) * uGRotRange);
  s.uv0 = s.r0 * uv + gHash22(v0 + 17.3);
  s.uv1 = s.r1 * uv + gHash22(v1 + 17.3);
  s.uv2 = s.r2 * uv + gHash22(v2 + 17.3);
  w = w * w;                                                          // a little sharper, less ghosting
  s.w = w / (w.x + w.y + w.z);
  return s;
}
// Contrast-preserving blend (Heitz & Neyret): average around the texture mean, then rescale so
// blending three samples does not wash the texture out towards grey.
vec4 gBlend(sampler2D t, GTri s, vec2 dx, vec2 dy, vec4 m) {
  vec4 c0 = textureGrad(t, s.uv0, s.r0 * dx, s.r0 * dy);
  vec4 c1 = textureGrad(t, s.uv1, s.r1 * dx, s.r1 * dy);
  vec4 c2 = textureGrad(t, s.uv2, s.r2 * dx, s.r2 * dy);
  vec4 d = s.w.x * (c0 - m) + s.w.y * (c1 - m) + s.w.z * (c2 - m);
  return m + d * inversesqrt(dot(s.w, s.w));
}
// Same for a tangent-space normal map: blend the xy slopes, rotated back into the shared frame.
vec2 gBlendNormal(sampler2D t, GTri s, vec2 dx, vec2 dy) {
  vec2 n0 = textureGrad(t, s.uv0, s.r0 * dx, s.r0 * dy).xy * 2.0 - 1.0;
  vec2 n1 = textureGrad(t, s.uv1, s.r1 * dx, s.r1 * dy).xy * 2.0 - 1.0;
  vec2 n2 = textureGrad(t, s.uv2, s.r2 * dx, s.r2 * dy).xy * 2.0 - 1.0;
  n0 = n0 * s.r0; n1 = n1 * s.r1; n2 = n2 * s.r2;                   // v * M = transpose(M) * v
  vec2 d = s.w.x * n0 + s.w.y * n1 + s.w.z * n2;
  return d * inversesqrt(dot(s.w, s.w));
}
`;

const GROUND_FRAG_COLOR = /* glsl */`
#include <color_fragment>
{
  float dist = length(vViewPosition);
  // Broad, world-space variation: drier and wetter patches a few metres across.
  float mN = 0.55 * gVnoise(vGPos.xz / 9.0) + 0.30 * gVnoise(vGPos.xz / 3.3 + 11.7) + 0.15 * gVnoise(vGPos.xz / 1.25 + 5.1);
  float macroLum = 0.88 + 0.24 * mN;
  gWet = 1.0 - smoothstep(uGWetY - ${WET_SOAK_BELOW.toFixed(3)}, uGWetY + ${WET_FADE_ABOVE.toFixed(3)}, vGPos.y);
  gRoughF = 1.0;
  gNormTS = vec3(0.0, 0.0, 1.0);
  if (uGReady < 0.5) {
    diffuseColor.rgb = vColor.rgb * macroLum;
  } else {
    vec3 ratio = clamp(vColor.rgb / uGMacroMean, vec3(0.5), vec3(1.7));
    vec2 uvW = vec2(vGPos.x, -vGPos.z);                               // u = +x, v = -z (= sim +y)
    // main layer
    vec2 uv1 = uvW * uGTile;
    vec2 dx1 = dFdx(uv1), dy1 = dFdy(uv1);
    GTri s1 = gMakeTri(uv1);
    vec3 alb = gBlend(uGDiff, s1, dx1, dy1, vec4(uGMean, 0.0)).rgb;
    vec2 rma = gBlend(uGRma, s1, dx1, dy1, vec4(uGMeanRma, 0.0, 0.0)).rg;
    vec2 nxy = gBlendNormal(uGNor, s1, dx1, dy1);
    // fine overlay, only near the camera: adds grain in brightness, relief and occlusion
    float fineW = 1.0 - smoothstep(uGFineFade.x, uGFineFade.y, dist);
    if (fineW > 0.002) {
      vec2 uv2 = uvW * uGFineTile;
      vec2 dx2 = dFdx(uv2), dy2 = dFdy(uv2);
      GTri s2 = gMakeTri(uv2 + 3.7);
      vec3 fa = gBlend(uGDiff, s2, dx2, dy2, vec4(uGMean, 0.0)).rgb;
      float lf = dot(fa, vec3(0.2126, 0.7152, 0.0722)) / uGMeanLum;
      alb *= mix(1.0, lf, uGFineLum * fineW);
      nxy += gBlendNormal(uGNor, s2, dx2, dy2) * (0.8 * fineW);
      // (the fine layer no longer re-samples the packed AO / roughness map: three fetches saved per pixel)
    }
    // distance: relief and colour contrast fade towards the average so far ground is calm, not shimmering
    float nFade = 1.0 - smoothstep(uGNormalFade.x, uGNormalFade.y, dist);
    float cFade = 1.0 - smoothstep(uGColorFade.x, uGColorFade.y, dist);
    alb = mix(uGMean, alb, mix(0.3, 1.0, cFade));
    nxy *= uGNormal * nFade;
    gNormTS = normalize(vec3(nxy, 1.0));
    float ao = clamp(rma.x, 0.0, 1.0);
    alb *= mix(1.0, ao / max(uGMeanRma.x, 0.05), uGAo);                // relative to the average, so overall brightness holds
    gRoughF = clamp(uGRoughMin + (1.0 - uGRoughMin) * clamp(rma.y, 0.0, 1.0), 0.0, 1.0) * (1.0 - 0.05 * (1.0 - mN));
    // The relief that fades out with distance is still there in reality, just too small to see. Its
    // effect on light is scatter, so as the normal map fades the surface gets rougher, never glossier.
    gRoughF = mix(gRoughF, 1.0, 1.0 - nFade);
    diffuseColor.rgb = alb * mix(vec3(1.0), ratio, uGMacroMix) * macroLum * uGAlbedo;
  }
  // Wet sand: darker and glossier. The darkening is a little stronger in the colour than a
  // straight multiply because water fills the pores and the grains' scattering drops.
  diffuseColor.rgb *= mix(1.0, ${WET_DARKEN.toFixed(3)}, gWet);
  gRoughF *= mix(1.0, ${WET_ROUGH.toFixed(3)}, gWet);
}
`;

const GROUND_FRAG_ROUGH = /* glsl */`
float roughnessFactor = roughness * gRoughF;
`;

// The ground's surface relief: perturb the lit normal with the tangent-space normal, using a
// frame built from the world axes (u = +x, v = -z, so the map's +Y faces sim +y).
// Dry litter is matte. Grazing angles (Fresnel) and the sky's reflection are what make a flat
// surface look wet or plastic, so the ground's specular is turned down as a whole.
const GROUND_FRAG_SPEC = /* glsl */`
#include <lights_fragment_end>
reflectedLight.directSpecular   *= uGSpec * mix(1.0, ${WET_SPEC.toFixed(2)}, gWet);
reflectedLight.indirectSpecular *= uGSpec * mix(1.0, ${WET_SPEC.toFixed(2)}, gWet);
${canopyShadeApply('vGPos')}
`;

const GROUND_FRAG_NORMAL = /* glsl */`
#include <normal_fragment_maps>
{
  vec3 nW = inverseTransformDirection(normal, viewMatrix);
  vec3 T = normalize(vec3(1.0, 0.0, 0.0) - nW * nW.x);
  vec3 B = cross(nW, T);
  vec3 pW = normalize(T * gNormTS.x + B * gNormTS.y + nW * gNormTS.z);
  normal = normalize((viewMatrix * vec4(pW, 0.0)).xyz);
}
`;

export class TerrainView {
  private readonly scene: THREE.Scene;
  private readonly plane: THREE.Mesh;          // the flat fallback, lowered under the terrain
  private readonly grid: THREE.Object3D | null;
  private mesh: THREE.Mesh | null = null;
  private z: Float32Array | null = null;
  private nx = 0; private ny = 0;
  private width = 0; private height = 0;
  private textures: { diff: THREE.Texture; nor: THREE.Texture; rma: THREE.Texture } | null = null;
  private readonly groundReady = { value: 0 };   // shared with the shader; 1 once the textures decode
  private readonly wetY = { value: -1e3 };        // shared with the shader; three-y of the wet level
  private sea = 0;                                // sea level (sim z) from the terrain message
  private tideAmp = 0;                            // peak tide excursion in metres
  private zMin = 0;                               // lowest ground (the seabed on an island)

  private readonly shade: CanopyShade;
  private readonly ownsShade: boolean;

  /** `shade` (optional) scales the lit ground by the plant canopy's shading map. Without one the
   *  shader still compiles the same, with a neutral map that leaves the ground unshaded. */
  constructor(scene: THREE.Scene, plane: THREE.Mesh, grid: THREE.Object3D | null, shade: CanopyShade | null = null) {
    this.scene = scene; this.plane = plane; this.grid = grid;
    this.ownsShade = shade === null;
    this.shade = shade ?? new CanopyShade(1, 1);
  }

  get loaded(): boolean { return this.z !== null; }
  /** Sea level (sim z = three y) before tides, from the server's terrain message. */
  get seaLevel(): number { return this.sea; }
  /** Peak tide excursion in metres (0 for a world with no sea). */
  get tideAmplitude(): number { return this.tideAmp; }
  /** True when the ground dips below sea level somewhere, i.e. the world has an ocean to draw. */
  get hasSea(): boolean { return this.z !== null && this.zMin < this.sea - 0.05; }
  get minHeight(): number { return this.zMin; }
  /** Three-y of the wet-sand level. The renderer eases it: up with the tide at once, down slowly. */
  setWetLevel(y: number) { this.wetY.value = y; }

  private loadTextures() {
    if (this.textures) return this.textures;
    const loader = new THREE.TextureLoader();
    let pending = 3;
    const done = () => { if (--pending === 0) this.groundReady.value = 1; };
    const tex = (src: string, srgb: boolean) => {
      const t = loader.load(src, done, undefined, () => console.warn('[sim] ground texture failed to load'));
      t.colorSpace = srgb ? THREE.SRGBColorSpace : THREE.NoColorSpace;
      t.wrapS = t.wrapT = THREE.RepeatWrapping;
      t.anisotropy = 8;
      return t;
    };
    this.textures = {
      diff: tex(GROUND_TEXTURES.diff, true),
      nor: tex(GROUND_TEXTURES.nor, false),
      rma: tex(GROUND_TEXTURES.rma, false),
    };
    return this.textures;
  }

  private makeMaterial(macroMean: THREE.Color): THREE.MeshStandardMaterial {
    const tx = this.loadTextures();
    const mat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 1.0, metalness: 0.0 });
    const u = {
      uGDiff: { value: tx.diff }, uGNor: { value: tx.nor }, uGRma: { value: tx.rma },
      uGMean: { value: new THREE.Vector3(...GROUND_STATS.meanColor) },
      uGMeanLum: { value: GROUND_STATS.meanLum },
      uGMeanRma: { value: new THREE.Vector2(GROUND_STATS.meanAo, GROUND_STATS.meanRough) },
      uGMacroMean: { value: new THREE.Vector3(macroMean.r, macroMean.g, macroMean.b) },
      uGTile: { value: 1 / GROUND_TILE_M }, uGFineTile: { value: 1 / GROUND_FINE_TILE_M },
      uGNormal: { value: GROUND_NORMAL }, uGMacroMix: { value: GROUND_MACRO_MIX },
      uGFineLum: { value: GROUND_FINE_LUM }, uGAo: { value: GROUND_AO }, uGRoughMin: { value: GROUND_ROUGH_MIN }, uGSpec: { value: GROUND_SPECULAR },
      uGFineFade: { value: new THREE.Vector2(...FINE_FADE) },
      uGNormalFade: { value: new THREE.Vector2(...NORMAL_FADE) },
      uGColorFade: { value: new THREE.Vector2(...COLOR_FADE) },
      uGReady: this.groundReady,
      uGWetY: this.wetY,
      uGRotRange: { value: GROUND_ROT_RANGE },
      uGAlbedo: { value: GROUND_ALBEDO },
    };
    mat.onBeforeCompile = (shader) => {
      Object.assign(shader.uniforms, u);
      Object.assign(shader.uniforms, this.shade.uniforms);
      shader.vertexShader = shader.vertexShader
        .replace('#include <common>', GROUND_VERT_COMMON)
        .replace('#include <begin_vertex>', GROUND_VERT_BEGIN);
      shader.fragmentShader = shader.fragmentShader
        .replace('#include <common>', GROUND_FRAG_COMMON)
        .replace('#include <color_fragment>', GROUND_FRAG_COLOR)
        .replace('#include <roughnessmap_fragment>', GROUND_FRAG_ROUGH)
        .replace('#include <lights_fragment_end>', GROUND_FRAG_SPEC)
        .replace('#include <normal_fragment_maps>', GROUND_FRAG_NORMAL);
    };
    mat.customProgramCacheKey = () => 'terrain-ground-v5-sand';
    return mat;
  }

  load(msg: TerrainMessage) {
    const bin = atob(msg.z);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    const z = new Float32Array(bytes.buffer);
    const { nx, ny } = msg;
    if (z.length !== nx * ny) { console.warn('[sim] terrain: size mismatch, ignoring'); return; }
    this.dispose(false);
    this.z = z; this.nx = nx; this.ny = ny; this.width = msg.width; this.height = msg.height;
    this.sea = msg.sea_level ?? 0;
    this.tideAmp = msg.tide_amplitude ?? 0;
    this.wetY.value = this.sea;

    const dx = this.width / (nx - 1), dy = this.height / (ny - 1);
    const pos = new Float32Array(nx * ny * 3);
    const col = new Float32Array(nx * ny * 3);
    const c = new THREE.Color(), lo = new THREE.Color(), hi = new THREE.Color(), sl = new THREE.Color();
    lo.setHex(SOIL_LOW); hi.setHex(SOIL_HIGH); sl.setHex(SLOPE_TINT);
    let zmin = 1e6;
    for (let i = 0; i < z.length; i++) if (z[i] < zmin) zmin = z[i];
    this.zMin = zmin;
    let mr = 0, mg = 0, mb = 0;
    for (let j = 0; j < ny; j++) {
      for (let i = 0; i < nx; i++) {
        const k = j * nx + i;
        const h = z[k];
        pos[k * 3] = i * dx; pos[k * 3 + 1] = h; pos[k * 3 + 2] = -j * dy;   // sim (x,y,z) -> three (x,z,-y)
        // slope from central differences
        const hx = (z[j * nx + Math.min(nx - 1, i + 1)] - z[j * nx + Math.max(0, i - 1)]) / (dx * (i > 0 && i < nx - 1 ? 2 : 1));
        const hy = (z[Math.min(ny - 1, j + 1) * nx + i] - z[Math.max(0, j - 1) * nx + i]) / (dy * (j > 0 && j < ny - 1 ? 2 : 1));
        const slope = Math.min(1, Math.hypot(hx, hy) / 0.14);             // 0.14 ~ 8 degrees
        // damp sand at the waterline, drying to full colour a metre above it
        c.copy(lo).lerp(hi, Math.min(1, Math.max(0, (h - this.sea + WET_BAND_BELOW) / (WET_BAND_ABOVE + WET_BAND_BELOW))));
        c.lerp(sl, slope * 0.6);
        const v = 1 - VERTEX_MOTTLE + 2 * VERTEX_MOTTLE * hash2(i, j);    // per-vertex mottling
        col[k * 3] = c.r * v; col[k * 3 + 1] = c.g * v; col[k * 3 + 2] = c.b * v;
        mr += col[k * 3]; mg += col[k * 3 + 1]; mb += col[k * 3 + 2];
      }
    }
    const nv = Math.max(1, nx * ny);
    const macroMean = new THREE.Color(mr / nv, mg / nv, mb / nv);
    const idx = new Uint32Array((nx - 1) * (ny - 1) * 6);
    let t = 0;
    for (let j = 0; j < ny - 1; j++) {
      for (let i = 0; i < nx - 1; i++) {
        const a = j * nx + i, b = a + 1, d = a + nx, e = d + 1;
        // counter-clockwise seen from above (three +y)
        idx[t++] = a; idx[t++] = b; idx[t++] = d;
        idx[t++] = b; idx[t++] = e; idx[t++] = d;
      }
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    g.setAttribute('color', new THREE.BufferAttribute(col, 3));
    g.setIndex(new THREE.BufferAttribute(idx, 1));
    g.computeVertexNormals();
    const mat = this.makeMaterial(macroMean);
    const mesh = new THREE.Mesh(g, mat);
    mesh.receiveShadow = true;
    mesh.castShadow = true;          // hills shade each other and the plain
    mesh.frustumCulled = false;
    this.scene.add(mesh);
    this.mesh = mesh;

    // The flat plane stays as the apron beyond the walls, just below z = 0 so the two never
    // fight where the terrain fades to zero at the world edge. The debug grid would
    // z-fight with the surface, so it goes.
    // On an island the plane must sit below the seabed: the sea is drawn over everything, and an
    // apron at -0.04 would show through the shallows as a ledge beyond the world edge.
    this.plane.position.y = zmin < this.sea - 0.05 ? zmin - 0.5 : -0.04;
    if (this.grid) this.grid.visible = false;
  }

  /** The raw heightfield (sim z, row = sim y), for other views that need the ground (grass). */
  field(): { z: Float32Array; nx: number; ny: number; width: number; height: number } | null {
    return this.z ? { z: this.z, nx: this.nx, ny: this.ny, width: this.width, height: this.height } : null;
  }

  /** Ground height (three-y) under three-space (x, z); 0 when no terrain is loaded. */
  heightAtThree(x: number, zThree: number): number {
    const z = this.z;
    if (!z) return 0;
    const fx = Math.min(Math.max(x / (this.width / (this.nx - 1)), 0), this.nx - 1.000001);
    const fy = Math.min(Math.max(-zThree / (this.height / (this.ny - 1)), 0), this.ny - 1.000001);
    const i = Math.floor(fx), j = Math.floor(fy);
    const tx = fx - i, ty = fy - j;
    const a = z[j * this.nx + i], b = z[j * this.nx + i + 1];
    const c = z[(j + 1) * this.nx + i], d = z[(j + 1) * this.nx + i + 1];
    return (a * (1 - tx) + b * tx) * (1 - ty) + (c * (1 - tx) + d * tx) * ty;
  }

  dispose(restore = true) {
    if (this.mesh) {
      this.scene.remove(this.mesh);
      this.mesh.geometry.dispose();
      (this.mesh.material as THREE.Material).dispose();
      this.mesh = null;
    }
    if (restore) {
      this.z = null;
      this.zMin = 0; this.sea = 0; this.tideAmp = 0;
      this.plane.position.y = 0;
      if (this.grid) this.grid.visible = true;
      if (this.textures) {
        this.textures.diff.dispose(); this.textures.nor.dispose(); this.textures.rma.dispose();
        this.textures = null;
        this.groundReady.value = 0;
      }
      if (this.ownsShade) this.shade.dispose();
    }
  }
}
